import React from "react";
import { useNavigate } from "react-router-dom";
import { TrendingUp, Star, FileCheck2, CheckCircle2, Shield, Eye, Award, GraduationCap, Sparkles, ChevronDown, ChevronUp, MapPin, ShoppingCart } from "lucide-react";
import { useState, useMemo, useEffect, useRef } from "react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { successStories, verifiedReports, whatDrivesUs, addOns, faqs } from "../mock";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { useCart } from "../contexts/CartContext";
import { useToast } from "../hooks/use-toast";

const driveIcons = { Shield, Eye, Award, GraduationCap };

// Animated counter for the After score - runs once card enters viewport
const AnimatedScore = ({ from, to }) => {
  const [n, setN] = useState(from);
  const ref = useRef(null);
  const startedRef = useRef(false);
  useEffect(() => {
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting && !startedRef.current) {
          startedRef.current = true;
          const t0 = performance.now();
          const dur = 1800;
          const tick = (t) => {
            const p = Math.min(1, (t - t0) / dur);
            const eased = 1 - Math.pow(1 - p, 3);
            setN(Math.round(from + (to - from) * eased));
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      });
    }, { threshold: 0.4 });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [from, to]);
  return <span ref={ref}>{n}</span>;
};

// ============ SUCCESS STORIES ============
export const SuccessStories = () => {
  const [visible, setVisible] = useState(6);
  const stories = successStories.slice(0, visible);

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center max-w-5xl mx-auto mb-12">
          <p className="text-knight-gold font-semibold tracking-widest text-xs mb-3">SUCCESS STORIES</p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-knight-navy mb-4 text-center whitespace-nowrap">Real credit score improvements</h2>
          <p className="text-knight-navy/70 leading-relaxed text-center whitespace-nowrap overflow-x-auto">See the actual score improvements our clients have achieved with our proven dispute strategies.</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {stories.map((s, i) => {
            const gain = s.after - s.before;
            return (
              <div key={i} className="group relative bg-white rounded-3xl border border-slate-200 overflow-hidden card-intense">
                <div className="p-7">
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-11 h-11 rounded-full bg-gradient-to-br from-knight-navy to-knight-navy-light flex items-center justify-center flex-shrink-0 ring-2 ring-knight-gold/30">
                        <MapPin size={18} className="text-knight-gold" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-bold text-knight-navy truncate">{s.name}, <span className="font-normal text-knight-navy/70">{s.state}</span></p>
                        <p className="text-xs text-knight-navy/50">Achieved in {s.months} months</p>
                      </div>
                    </div>
                    <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 border border-emerald-200 font-bold flex-shrink-0">+{gain}</Badge>
                  </div>

                  <div className="grid grid-cols-3 items-center gap-2 bg-slate-50 rounded-2xl p-4 mb-5">
                    <div className="text-center">
                      <p className="text-[10px] tracking-widest text-knight-navy/50 font-bold mb-1">BEFORE</p>
                      <p className="font-display text-3xl font-bold text-red-500">{s.before}</p>
                    </div>
                    <div className="text-center flex flex-col items-center">
                      <TrendingUp className="text-emerald-600" size={22} />
                      <span className="text-[10px] font-bold text-emerald-700 mt-0.5">+{gain} pts</span>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] tracking-widest text-knight-navy/50 font-bold mb-1">AFTER</p>
                      <p className="font-display text-4xl font-extrabold text-emerald-600 drop-shadow-sm">
                        <AnimatedScore from={s.before} to={s.after} />
                      </p>
                    </div>
                  </div>

                  <p className="text-sm text-knight-navy/75 italic leading-relaxed">&ldquo;{s.quote}&rdquo;</p>
                  <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map(k => <Star key={k} size={13} fill="#f4b400" stroke="#f4b400" />)}
                    </div>
                    <span className="text-[10px] text-knight-navy/50 font-semibold">Achieved in {s.months} months</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {visible < successStories.length && (
          <div className="text-center mt-10">
            <Button onClick={() => setVisible(successStories.length)} variant="outline" className="border-knight-navy text-knight-navy hover:bg-knight-navy hover:text-white">
              Load More Stories
            </Button>
          </div>
        )}
        <p className="text-center text-xs text-knight-navy/50 mt-8 italic">*Results vary based on individual credit profiles and are not guaranteed.</p>
      </div>
    </section>
  );
};

// ============ VERIFIED CREDIT REPORTS ============
// Stylized report-card visualization (mocked) — no real client documents.
const MockReportVisual = ({ points, deletions, bureau }) => (
  <div className="aspect-[4/5] bg-gradient-to-br from-slate-50 to-slate-100 rounded-t-2xl border-b border-slate-200 p-5 relative overflow-hidden">
    <div className="flex items-center justify-between text-[10px] font-bold text-knight-navy/40 tracking-wider mb-3">
      <span>CREDIT REPORT</span>
      <span>{bureau.toUpperCase()}</span>
    </div>
    {/* Mock report rows */}
    <div className="space-y-2">
      {[0, 1, 2, 3, 4, 5].map((i) => {
        const removed = i < 3;
        return (
          <div key={i} className={`flex items-center justify-between px-3 py-2 rounded-lg text-[10px] ${removed ? "bg-red-50 line-through text-red-400" : "bg-white border border-slate-200 text-knight-navy/70"}`}>
            <div className="flex items-center gap-2">
              <div className={`w-1.5 h-1.5 rounded-full ${removed ? "bg-red-400" : "bg-emerald-500"}`} />
              <span className="font-semibold">{["Collection", "Late Pmt", "Charge-Off", "Inquiry", "Tradeline", "Account"][i]}</span>
            </div>
            <span className="font-mono">{removed ? "DELETED" : "VERIFIED"}</span>
          </div>
        );
      })}
    </div>
    <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-knight-gold/10 rounded-full blur-2xl" />
    <div className="absolute top-3 right-3 bg-emerald-100 text-emerald-700 text-[9px] font-bold px-2 py-0.5 rounded-full border border-emerald-200">
      +{points} PTS
    </div>
  </div>
);

export const VerifiedReports = () => (
  <section className="py-20 subtle-gradient">
    <div className="max-w-7xl mx-auto px-4">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <Badge className="bg-knight-navy/5 text-knight-navy hover:bg-knight-navy/5 border border-knight-navy/10 mb-4">
          <FileCheck2 size={12} className="mr-1.5" /> VERIFIED RESULTS
        </Badge>
        <h2 className="font-display text-4xl lg:text-5xl font-bold text-knight-navy mb-4">Verified Credit <em>Reports</em></h2>
        <p className="text-knight-navy/70 leading-relaxed">Transparency is our priority. Each card below summarizes a real client outcome \u2014 with deletions, score gains, and the bureau where progress was secured.</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {verifiedReports.map((r, i) => (
          <div key={i} className="bg-white rounded-2xl shadow-md border border-slate-200 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 overflow-hidden">
            <MockReportVisual {...r} />
            <div className="p-4">
              <div className="flex items-baseline justify-between mb-1">
                <span className="font-display text-2xl font-bold text-emerald-600">+{r.points} pts</span>
                <span className="text-xs font-semibold text-knight-navy/50">{r.duration}</span>
              </div>
              <p className="text-xs text-knight-navy/70 font-medium">{r.deletions}</p>
            </div>
          </div>
        ))}
      </div>

      <p className="text-center text-xs text-knight-navy/50 mt-10 italic max-w-2xl mx-auto">
        *Illustrative summaries of real client results. Individual outcomes vary based on credit profile and reporting accuracy.
      </p>
    </div>
  </section>
);

// ============ WHAT DRIVES US (Our Values) — dynamic, animated cards ============
export const WhatDrivesUs = () => {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) setVisible(true);
    }, { threshold: 0.15 });
    if (sectionRef.current) io.observe(sectionRef.current);
    return () => io.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative py-24 overflow-hidden bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Decorative orbs */}
      <div className="absolute -top-32 -left-32 w-[420px] h-[420px] bg-knight-gold/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 w-[420px] h-[420px] bg-knight-navy/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 bg-knight-navy/5 border border-knight-navy/10 rounded-full px-4 py-1.5 mb-4">
            <Sparkles size={14} className="text-knight-gold" />
            <p className="text-knight-navy font-semibold tracking-widest text-xs">WHAT DRIVES US</p>
          </div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-knight-navy mb-4 text-center whitespace-nowrap">Our Values</h2>
          <p className="text-knight-navy/70 leading-relaxed">The principles that guide every dispute, every conversation, every outcome.</p>
        </div>

        {/* Auto-scrolling marquee — pauses on hover */}
        <div
          className={`relative overflow-hidden group transition-all duration-700 ${visible ? "opacity-100" : "opacity-0"}`}
          style={{ maskImage: "linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)", WebkitMaskImage: "linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)" }}
        >
          <div className="flex gap-6 w-max animate-marquee group-hover:[animation-play-state:paused]">
            {[...whatDrivesUs, ...whatDrivesUs].map((v, i) => {
              const Icon = driveIcons[v.icon];
              return (
                <div
                  key={i}
                  className="relative w-[320px] flex-shrink-0 perspective"
                >
                  {/* Animated gradient border wrapper */}
                  <div className="relative rounded-3xl p-[2px] overflow-hidden bg-gradient-to-br from-knight-gold/40 via-knight-navy/20 to-knight-gold/40 hover:from-knight-gold hover:to-knight-gold transition-all duration-500 group/card">
                    <div className="relative bg-white rounded-[22px] p-7 h-full overflow-hidden">
                      <div className="absolute top-3 right-4 font-display text-7xl font-extrabold text-slate-100 select-none group-hover/card:text-knight-gold/15 transition-colors">
                        0{(i % whatDrivesUs.length) + 1}
                      </div>
                      <div className="relative inline-flex items-center justify-center mb-5">
                        <div className="absolute inset-0 rounded-2xl bg-knight-gold/25 blur-xl scale-110 opacity-0 group-hover/card:opacity-100 transition-opacity duration-500" />
                        <div className="relative w-16 h-16 rounded-2xl bg-knight-navy text-knight-gold flex items-center justify-center group-hover/card:bg-knight-gold group-hover/card:text-knight-navy transition-all duration-500 group-hover/card:-rotate-6 group-hover/card:scale-110">
                          <Icon size={28} />
                        </div>
                      </div>
                      <h3 className="relative font-display font-bold text-xl text-knight-navy mb-2">{v.title}</h3>
                      <p className="relative text-sm text-knight-navy/75 leading-relaxed">{v.text}</p>
                      <div className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-knight-gold to-knight-gold/30 w-0 group-hover/card:w-full transition-all duration-500" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

// ============ ADD-ONS ============
export const AddOns = () => {
  const { addItem } = useCart();
  const { toast } = useToast();
  const navigate = useNavigate();
  const add = (a) => {
    addItem({ id: `addon-${a.name.toLowerCase().replace(/\s+/g, "-")}`, type: "addon", name: a.name, oneTime: a.price, description: a.description });
    toast({ title: "Added to cart", description: `${a.name} added to your order.` });
    navigate("/checkout");
  };
  return (
  <section className="py-20 subtle-gradient">
    <div className="max-w-7xl mx-auto px-4">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <p className="text-knight-gold font-semibold tracking-widest text-xs mb-3">ADD-ON SERVICES</p>
        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-knight-navy mb-4 text-center whitespace-nowrap">Specialized Solutions</h2>
        <p className="text-knight-navy/70 leading-relaxed">Targeted services for specific credit challenges. Purchase standalone or bundle with any plan.</p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
        {addOns.map((a, i) => (
          <div key={i} className="card-intense card-amplify relative bg-white rounded-3xl border-2 border-slate-200 p-7 flex flex-col">
            {a.badge && (
              <Badge className="absolute -top-3 left-7 bg-knight-gold text-knight-navy font-bold px-3 py-1 z-10">
                <Sparkles size={11} className="mr-1" /> {a.badge}
              </Badge>
            )}
            <h3 className="font-display font-bold text-2xl text-knight-navy mb-2 transition-transform duration-300 group-hover:translate-x-1">{a.name}</h3>
            <div className="flex items-baseline gap-1.5 mb-4">
              <span className="font-display text-4xl font-bold text-knight-navy">${a.price}</span>
              <span className="text-sm text-knight-navy/55 font-medium">one-time</span>
            </div>
            <p className="text-sm text-knight-navy/70 leading-relaxed mb-5">{a.description}</p>
            <ul className="space-y-2.5 mb-6 flex-1">
              {a.features.map((f, k) => (
                <li key={k} className="flex items-start gap-2.5 text-sm text-knight-navy/80">
                  <CheckCircle2 size={16} className="text-knight-gold flex-shrink-0 mt-0.5" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <Button onClick={() => add(a)} className="w-full bg-knight-navy hover:bg-knight-navy-dark text-white font-semibold btn-lift">
              <ShoppingCart size={16} className="mr-2" /> Add to Cart
            </Button>
          </div>
        ))}
      </div>
    </div>
  </section>
  );
};

// ============ FAQ (expanded) ============
export const FAQEnhanced = () => (
  <section className="py-20 bg-white">
    <div className="max-w-4xl mx-auto px-4">
      <div className="text-center mb-12">
        <p className="text-knight-gold font-semibold tracking-widest text-xs mb-3">FAQ</p>
        <h2 className="font-display text-4xl lg:text-5xl font-bold text-knight-navy mb-3">Frequently Asked <em>Questions</em></h2>
        <p className="text-knight-navy/70">Everything you need to know about working with Knight Credit Consultants.</p>
      </div>
      <Accordion type="single" collapsible className="space-y-3">
        {faqs.map((f, i) => (
          <AccordionItem key={i} value={`item-${i}`} className="faq-hover border border-slate-200 rounded-xl px-5 data-[state=open]:border-knight-navy data-[state=open]:bg-slate-50/50 transition-colors">
            <AccordionTrigger className="text-left font-semibold text-knight-navy hover:no-underline">{f.q}</AccordionTrigger>
            <AccordionContent className="text-knight-navy/75 leading-relaxed">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);
